import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Hammer, Utensils, Trees, Wrench, Building2, Flame, PhoneCall,
  CheckCircle2, Download, RotateCcw, History, ExternalLink, Star, Globe,
  Phone, Loader2, MapPin, Filter, X, Smartphone, Plus, ChevronDown, Check,
  Headphones, Printer, RefreshCw, LayoutGrid, Table2, AlarmClock, Trash2,
  UserSearch, GraduationCap, ServerCog, Briefcase, AtSign, Linkedin, ShieldCheck,
  ClipboardCheck, TrendingUp,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { CallReportModal } from "@/components/CallReportModal";
import { PipelineBoard } from "@/components/PipelineBoard";
import GeoPicker from "@/components/GeoPicker";
import { STATUS_META, isOverdue, OVERDUE_CLS } from "@/lib/statuses";
import { printProspect, printCallSheet } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { NAF_CATALOG, NAF_CATEGORIES } from "@/lib/nafCatalog";

import { API } from "@/lib/api";


const ICONS = { Hammer, Utensils, Trees, Wrench, UserSearch, GraduationCap, ServerCog, Briefcase, ClipboardCheck };

export function formatCa(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(".0", "")} Md€`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".0", "")} M€`;
  if (n >= 1_000) return `${Math.round(n / 1_000)} k€`;
  return `${n} €`;
}

const WORKFORCE_OPTIONS = [
  { code: "00", label: "0 salarié" },
  { code: "01", label: "1-2 salariés" },
  { code: "02", label: "3-5 salariés" },
  { code: "03", label: "6-9 salariés" },
  { code: "11", label: "10-19 salariés" },
  { code: "12", label: "20-49 salariés" },
  { code: "21", label: "50-99 salariés" },
  { code: "22", label: "100-199 salariés" },
  { code: "31", label: "200-249 salariés" },
  { code: "32", label: "250-499 salariés" },
  { code: "41", label: "500-999 salariés" },
  { code: "42", label: "1000-1999 salariés" },
];


const MATCH_META = {
  haute: { label: "Haute", cls: "bg-brand-moss/10 text-brand-moss border-brand-moss/25" },
  moyenne: { label: "Moyenne", cls: "bg-brand-paper text-brand-ink border-brand-line" },
  faible: { label: "Faible", cls: "bg-white text-brand-subtle border-brand-line" },
};
const MATCH_UNKNOWN = { label: "—", cls: "bg-white text-brand-subtle border-brand-line" };

function scoreBadge(score) {
  if (score >= 75) return "bg-brand-moss text-white border-brand-moss";
  if (score >= 50) return "bg-brand-mossSoft text-brand-moss border-brand-moss/20";
  return "bg-white text-brand-subtle border-brand-line";
}

// Détecte si un numéro français est mobile (06/07), fixe, ou étranger
function phoneKind(phone) {
  if (!phone) return "aucun";
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("336") || digits.startsWith("337") || phone.trim().startsWith("06") || phone.trim().startsWith("07")) return "mobile";
  if (digits.startsWith("33") || phone.trim().startsWith("0")) return "fixe";
  return "etranger";
}

export default function Dashboard() {
  const [presets, setPresets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeGroups, setActiveGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("FR");
  const [activePreset, setActivePreset] = useState(null);
  const [nafInput, setNafInput] = useState("");
  const [geoSelection, setGeoSelection] = useState([]);
  const [workforceMin, setWorkforceMin] = useState("");
  const [workforceMax, setWorkforceMax] = useState("");
  const [ageMin, setAgeMin] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("all");
  const [caMin, setCaMin] = useState("");
  const [caMax, setCaMax] = useState("");
  const [targetCount, setTargetCount] = useState("100");
  const [expansion, setExpansion] = useState(null);
  // Hors France
  const [keywords, setKeywords] = useState("");
  const [cities, setCities] = useState("");

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalProspects, setTotalProspects] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchMode, setSearchMode] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [kpis, setKpis] = useState({ total: 0, high_priority: 0, enriched_rate: 0, to_call: 0 });
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [callTarget, setCallTarget] = useState(null);
  const [quotaKey, setQuotaKey] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [selectedSirets, setSelectedSirets] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [enrichingContacts, setEnrichingContacts] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Preset "Autre" : sélection multi de métiers dans le catalogue NAF complet
  const [customNafSelected, setCustomNafSelected] = useState([]);
  const [otherPickerOpen, setOtherPickerOpen] = useState(false);

  // Filtres tableau côté client
  const [fStatus, setFStatus] = useState("all");
  const [fPhoneKind, setFPhoneKind] = useState("all");
  const [fMinScore, setFMinScore] = useState("");
  const [fCity, setFCity] = useState("");
  const [fMatch, setFMatch] = useState("all");
  const [fWithWebsite, setFWithWebsite] = useState(false);

  const isFrance = country === "FR";

  const fetchKpis = useCallback(async () => {
    try { const r = await axios.get(`${API}/kpis`); setKpis(r.data); } catch (e) {}
  }, []);
  const fetchHistory = useCallback(async () => {
    try { const r = await axios.get(`${API}/search-history`); setHistory(r.data.results || []); } catch (e) {}
  }, []);

  const loadProspectsPage = useCallback(async (nextPage = 1) => {
    setListLoading(true);
    try {
      const r = await axios.get(`${API}/prospects`, { params: { page: nextPage, page_size: pageSize } });
      setResults(r.data.results || []);
      setPage(r.data.page || nextPage);
      setTotalProspects(r.data.total || 0);
      setTotalPages(r.data.pages || 1);
      setSearchMode(false);
      setSelectedSirets([]);
      // Remonte la vue en haut du tableau pour éviter de rester en bas de page.
      if (typeof window !== "undefined") {
        requestAnimationFrame(() => {
          const el = document.querySelector('[data-testid="prospects-table-body"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          else window.scrollTo({ top: 0, behavior: "smooth" });
        });
      }
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les prospects");
      return null;
    } finally { setListLoading(false); }
  }, [pageSize]);

  useEffect(() => {
    axios.get(`${API}/presets`).then((r) => {
      setPresets(r.data.presets || []);
      setGroups(r.data.groups || []);
    });
    axios.get(`${API}/countries`).then((r) => setCountries(r.data.countries || []));
    fetchKpis(); fetchHistory();
    loadProspectsPage(1);
    axios.get(`${API}/events/overdue`).then((r) => setOverdueCount(r.data.count || 0)).catch(() => {});
  }, [fetchKpis, fetchHistory, loadProspectsPage]);

  // Ouverture directe d'un prospect via ?siret= (depuis le calendrier).
  // Avec la pagination, le prospect peut ne pas être sur la page courante :
  // on fait alors un fetch ciblé sur /api/prospects/{siret}.
  // Dépend uniquement de `searchParams` pour éviter de re-fetcher à chaque
  // changement de page.
  useEffect(() => {
    const siret = searchParams.get("siret");
    if (!siret) return;
    let cancelled = false;
    axios.get(`${API}/prospects/${siret}`).then((r) => {
      if (cancelled) return;
      setSelectedLead(r.data);
    }).catch((e) => {
      if (!cancelled) {
        toast.error(e.response?.status === 404 ? "Prospect introuvable" : "Impossible d'ouvrir le prospect");
      }
    }).finally(() => {
      if (cancelled) return;
      searchParams.delete("siret");
      setSearchParams(searchParams, { replace: true });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Détail complet d'un prospect : les tableaux imbriqués (dirigeants, contacts
  // enrichis, emails/téléphones du site) ne sont pas dans la liste légère.
  // On fait un fetch ciblé à l'ouverture du drawer pour les récupérer.
  useEffect(() => {
    if (!selectedLead?.siret) return;
    if (Array.isArray(selectedLead.dirigeants) || Array.isArray(selectedLead.dirigeants_contacts)) return;
    let cancelled = false;
    axios.get(`${API}/prospects/${selectedLead.siret}`).then((r) => {
      if (cancelled) return;
      setSelectedLead((cur) => (cur && cur.siret === r.data.siret ? { ...cur, ...r.data } : cur));
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.siret]);

  const activeNafCodes = useMemo(() => {
    const codes = new Set();
    if (activePreset) {
      const p = presets.find((x) => x.id === activePreset);
      if (p) p.codes.forEach((c) => codes.add(c));
    }
    activeGroups.forEach((gid) => {
      const g = groups.find((x) => x.id === gid);
      if (g) g.codes.forEach((c) => codes.add(c));
    });
    customNafSelected.forEach((c) => codes.add(c));
    nafInput.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean).forEach((c) => codes.add(c));
    return [...codes];
  }, [activePreset, activeGroups, groups, nafInput, presets, customNafSelected]);

  const activeKeywords = useMemo(() => {
    const codes = new Set();
    if (activePreset) {
      const p = presets.find((x) => x.id === activePreset);
      if (p) {
        const label = p.label.toLowerCase();
        if (label.includes("menuisier")) codes.add("menuisier");
        if (label.includes("cuisiniste")) codes.add("cuisiniste");
        if (label.includes("paysagiste")) codes.add("paysagiste");
        if (label.includes("garage") || label.includes("carrossier")) codes.add("garage");
      }
    }
    keywords.split(/[,;]+/).map((s) => s.trim()).filter(Boolean).forEach((c) => codes.add(c));
    return [...codes];
  }, [activePreset, keywords, presets]);

  const runSearch = async () => {
    if (isFrance && activeNafCodes.length === 0) {
      toast.error("Sélectionnez un preset ou saisissez un code NAF");
      return;
    }
    if (!isFrance && activeKeywords.length === 0) {
      toast.error("Saisissez au moins un mot-clé métier (ex: menuisier)");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        country,
        naf_codes: isFrance ? activeNafCodes : [],
        keywords: !isFrance ? activeKeywords : [],
        cities: !isFrance
          ? cities.split(/[,;]+/).map((s) => s.trim()).filter(Boolean)
          : [],
        departments: isFrance
          ? [...new Set(geoSelection.flatMap((g) => g.departments || []))]
          : [],
        postal_codes: isFrance
          ? [...new Set(geoSelection.flatMap((g) => g.postal_codes || []))]
          : [],
        workforce_min: workforceMin || null,
        workforce_max: workforceMax || null,
        age_min_years: ageMin ? parseInt(ageMin, 10) : null,
        phone_filter: phoneFilter,
        ca_min: caMin ? parseInt(caMin, 10) : null,
        ca_max: caMax ? parseInt(caMax, 10) : null,
        target_count: Math.max(1, Math.min(100, parseInt(targetCount, 10) || 100)),
        limit: 100,
      };
      const r = await axios.post(`${API}/prospects/search`, payload);
      setResults(r.data.results || []);
      setSearchMode(true);
      setTotalProspects((r.data.results || []).length);
      setTotalPages(1);
      setPage(1);
      setExpansion(r.data.expansion || null);
      toast.success(`${r.data.count} entreprise${r.data.count > 1 ? "s" : ""} trouvée${r.data.count > 1 ? "s" : ""}` + (r.data.cached ? " (cache)" : ""));
      fetchKpis(); fetchHistory(); setQuotaKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la recherche");
    } finally { setLoading(false); }
  };

  const resetFilters = () => {
    setActivePreset(null); setNafInput(""); setGeoSelection([]);
    setActiveGroups([]);
    setWorkforceMin(""); setWorkforceMax(""); setAgeMin(""); setKeywords(""); setCities("");
    setCustomNafSelected([]); setPhoneFilter("all"); setTargetCount("100"); setExpansion(null);
    setCaMin(""); setCaMax("");
  };
  const resetTableFilters = () => {
    setFStatus("all"); setFPhoneKind("all"); setFMinScore(""); setFCity(""); setFMatch("all"); setFWithWebsite(false);
  };

  const filteredResults = useMemo(() => {
    const list = results.filter((p) => {
      if (fStatus !== "all" && p.status !== fStatus) return false;
      if (fMatch !== "all" && p.match_level !== fMatch) return false;
      if (fMinScore && p.priority_score < parseInt(fMinScore, 10)) return false;
      if (fCity && !(p.ville || "").toLowerCase().includes(fCity.toLowerCase())) return false;
      if (fWithWebsite && !p.google_website) return false;
      if (fPhoneKind !== "all") {
        const k = phoneKind(p.google_phone || "");
        if (fPhoneKind === "mobile" && k !== "mobile") return false;
        if (fPhoneKind === "fixe" && k !== "fixe") return false;
        if (fPhoneKind === "any" && !p.google_phone) return false;
      }
      return true;
    });
    // Mobile nominatif de dirigeant toujours en tête, puis score de priorité
    return list.sort((a, b) =>
      (b.has_direct_mobile ? 1 : 0) - (a.has_direct_mobile ? 1 : 0) ||
      (b.priority_score || 0) - (a.priority_score || 0)
    );
  }, [results, fStatus, fMatch, fMinScore, fCity, fPhoneKind, fWithWebsite]);

  const activeTableFilters =
    (fStatus !== "all" ? 1 : 0) + (fPhoneKind !== "all" ? 1 : 0) + (fMinScore ? 1 : 0) +
    (fCity ? 1 : 0) + (fMatch !== "all" ? 1 : 0) + (fWithWebsite ? 1 : 0);

  const updateStatus = async (siret, status) => {
    try {
      await axios.patch(`${API}/prospects/${siret}/status`, { status });
      setResults((prev) => prev.map((p) => (p.siret === siret ? { ...p, status } : p)));
      if (selectedLead?.siret === siret) setSelectedLead({ ...selectedLead, status });
      toast.success("Statut mis à jour"); fetchKpis();
    } catch { toast.error("Erreur"); }
  };

  const saveNotes = async (siret, notes) => {
    try {
      await axios.patch(`${API}/prospects/${siret}/notes`, { notes });
      setResults((prev) => prev.map((p) => (p.siret === siret ? { ...p, notes } : p)));
      toast.success("Notes sauvegardées");
    } catch { toast.error("Erreur"); }
  };

  const exportCsv = async (sirets = null) => {
    const password = window.prompt("Mot de passe d'export CSV :");
    if (!password) return;
    try {
      const list = (sirets || filteredResults.map((p) => p.siret)).filter(Boolean).join(",");
      const params = new URLSearchParams({ password });
      if (list && (sirets || activeTableFilters > 0)) params.set("sirets", list);
      const url = `${API}/prospects/export?${params.toString()}`;
      const r = await axios.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([r.data]));
      link.download = `studio-instant-prospects-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Export CSV téléchargé");
    } catch (e) {
      if (e.response?.status === 401) toast.error("Mot de passe incorrect");
      else toast.error("Erreur d'export");
    }
  };

  const allVisibleSelected = filteredResults.length > 0 &&
    filteredResults.every((p) => selectedSirets.includes(p.siret));

  const toggleSelectAll = () => {
    setSelectedSirets(allVisibleSelected ? [] : filteredResults.map((p) => p.siret));
  };

  const toggleSelect = (siret) => {
    setSelectedSirets((prev) => (prev.includes(siret) ? prev.filter((s) => s !== siret) : [...prev, siret]));
  };

  const applyBulkStatus = async (status) => {
    try {
      await axios.post(`${API}/prospects/bulk-status`, { sirets: selectedSirets, status });
      setResults((prev) => prev.map((p) => (selectedSirets.includes(p.siret) ? { ...p, status } : p)));
      toast.success(`${selectedSirets.length} prospect(s) mis à jour`);
      setBulkStatus(""); fetchKpis();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteSelected = async () => {
    try {
      const r = await axios.post(`${API}/prospects/bulk-delete`, { sirets: selectedSirets });
      const deletedSet = new Set(selectedSirets);
      if (selectedLead && deletedSet.has(selectedLead.siret)) setSelectedLead(null);
      toast.success(`${r.data.deleted} prospect(s) supprimé(s)`);
      setSelectedSirets([]); setConfirmDelete(false); fetchKpis();
      axios.get(`${API}/events/overdue`).then((res) => setOverdueCount(res.data.count || 0)).catch(() => {});
      // Recharge la page courante pour recalculer total/pages côté serveur.
      if (!searchMode) {
        const data = await loadProspectsPage(page);
        if (data && (data.count || 0) === 0 && page > 1) {
          await loadProspectsPage(page - 1);
        }
      } else {
        setResults((prev) => prev.filter((p) => !deletedSet.has(p.siret)));
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
  };

  const loadHistoryItem = (h) => {
    const p = h.params || {};
    setCountry(p.country || "FR");
    setNafInput((p.naf_codes || []).join(", "));
    // Reconstruit la sélection géo depuis l'historique
    const geo = [];
    (p.postal_codes || []).forEach((cp) => {
      if (cp) geo.push({ type: "city", code: `city-cp:${cp}`, label: cp, sublabel: "Code postal", departments: [], postal_codes: [cp] });
    });
    (p.departments || []).forEach((d) => {
      if (d) geo.push({ type: "department", code: `dept:${d}`, label: d, sublabel: `Département · ${d}`, departments: [d], postal_codes: [] });
    });
    setGeoSelection(geo);
    setWorkforceMin(p.workforce_min || ""); setWorkforceMax(p.workforce_max || "");
    setAgeMin(p.age_min_years ? String(p.age_min_years) : "");
    setKeywords((p.keywords || []).join(", "));
    setCities((p.cities || []).join(", "));
    setPhoneFilter(p.phone_filter || "all");
    setCaMin(p.ca_min ? String(p.ca_min) : "");
    setCaMax(p.ca_max ? String(p.ca_max) : "");
    setTargetCount(p.target_count ? String(p.target_count) : "100");
    setActivePreset(null); setCustomNafSelected([]); setHistoryOpen(false);
    toast.info("Recherche chargée — cliquez sur Lancer pour relancer");
  };

  const toggleCustomNaf = (code) => {
    setCustomNafSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const onCallSaved = ({ siret, status }) => {
    setResults((prev) => prev.map((p) => (p.siret === siret ? { ...p, status } : p)));
    if (selectedLead?.siret === siret) setSelectedLead((l) => ({ ...l, status }));
    fetchKpis();
  };

  const enrichContacts = async (siret) => {
    setEnrichingContacts(true);
    try {
      const r = await axios.post(`${API}/prospects/${siret}/enrich-contacts`);
      const fresh = r.data.prospect;
      setResults((prev) => prev.map((p) => (p.siret === siret ? { ...p, ...fresh } : p)));
      setSelectedLead((l) => ({ ...l, ...fresh }));
      const n = (fresh.site_emails?.length || 0) + (fresh.site_phones?.length || 0);
      toast.success(n ? `${n} coordonnée(s) publique(s) trouvée(s) sur le site` : "Aucune coordonnée publique trouvée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrichissement impossible");
    } finally { setEnrichingContacts(false); }
  };

  const bulkEnrichContacts = async () => {
    setEnrichingContacts(true);
    try {
      const r = await axios.post(`${API}/prospects/bulk-enrich-contacts`, { sirets: selectedSirets });
      const byS = Object.fromEntries((r.data.prospects || []).map((p) => [p.siret, p]));
      setResults((prev) => prev.map((p) => (byS[p.siret] ? { ...p, ...byS[p.siret] } : p)));
      toast.success(`${r.data.with_contacts} prospect(s) avec coordonnées publiques sur ${r.data.checked}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrichissement impossible");
    } finally { setEnrichingContacts(false); }
  };

  const refreshEnrichment = async (siret) => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/prospects/${siret}/refresh-enrichment`);
      const fresh = r.data.prospect;
      setResults((prev) => prev.map((p) => (p.siret === siret ? fresh : p)));
      setSelectedLead(fresh);
      setQuotaKey((k) => k + 1);
      toast.success(r.data.api_called ? "Enrichissement rafraîchi via Google Places" : "Données déjà à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rafraîchissement impossible");
    } finally { setRefreshing(false); }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <AppHeader
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} data-testid="btn-search-history"
              className="border-brand-line text-brand-ink hover:bg-brand-paper hover:border-brand-ink/20 rounded-md">
              <History className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Historique</span>
            </Button>
            <Button size="sm" onClick={() => exportCsv()} data-testid="btn-export-csv"
              className="bg-brand-moss hover:bg-brand-moss2 text-white rounded-md">
              <Download className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Export CSV</span>
            </Button>
          </>
        }
      />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
          <KpiCard icon={Building2} label="Entreprises identifiées" value={kpis.total} testId="kpi-total" />
          <KpiCard icon={CheckCircle2} label="Enrichi Google" value={`${kpis.enriched_rate}%`} testId="kpi-enriched" />
          <KpiCard icon={Flame} label="Priorité haute" value={kpis.high_priority} accent testId="kpi-high" />
          <KpiCard icon={PhoneCall} label="À appeler" value={kpis.to_call} testId="kpi-tocall" />
        </div>

        {/* Search Panel */}
        <Card className="p-5 sm:p-8 mb-6 sm:mb-8 bg-white border-brand-line rounded-md shadow-card">
          <div className="flex items-center justify-between mb-6 pb-5 border-b border-brand-line">
            <div>
              <div className="font-display text-xl sm:text-2xl text-brand-ink leading-none">Recherche de prospects</div>
              <div className="eyebrow mt-2">SIRENE × Google Places</div>
            </div>
            <div className="flex items-center gap-2">
              <Label className="eyebrow hidden sm:block">Pays</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="search-select-country" className="h-9 w-40 text-xs border-brand-line rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Groupes de métiers (bundles de presets) */}
          {isFrance && groups.length > 0 && (
            <div className="mb-4">
              <div className="eyebrow mb-2.5">Groupes de métiers</div>
              <div className="flex flex-wrap gap-2">
                {groups.map((g) => {
                  const Icon = ICONS[g.icon] || Hammer;
                  const active = activeGroups.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      onClick={() =>
                        setActiveGroups((cur) =>
                          cur.includes(g.id) ? cur.filter((x) => x !== g.id) : [...cur, g.id]
                        )
                      }
                      data-testid={`btn-preset-group-${g.id}`}
                      title={g.description}
                      className={`inline-flex items-center gap-2 rounded-md px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs font-medium border transition-all ${
                        active
                          ? "bg-brand-ink text-white border-brand-ink shadow-sm"
                          : "bg-white text-brand-ink/85 border-brand-line hover:border-brand-ink/25"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span>{g.label}</span>
                      <span className="font-mono text-[10px] opacity-70">
                        {g.preset_ids.length} métiers · {g.codes.length} NAF
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="h-px bg-brand-line/70 mt-4" />
            </div>
          )}

          {/* Presets individuels */}
          <div className="flex flex-wrap gap-2 mb-5">
            {presets.map((p) => {
              const Icon = ICONS[p.icon] || Hammer;
              const active = activePreset === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setActivePreset(active ? null : p.id)}
                  data-testid={`btn-preset-naf-${p.id}`}
                  className={`inline-flex items-center gap-2 rounded-md px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs font-medium border transition-all ${
                    active
                      ? "bg-brand-moss text-white border-brand-moss shadow-sm"
                      : "bg-white text-brand-ink/85 border-brand-line hover:border-brand-ink/25"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{p.label}</span>
                  {isFrance && <span className="font-mono text-[10px] opacity-70 hidden md:inline">{p.codes.join("·")}</span>}
                </button>
              );
            })}

            {/* Preset "Autre" : recherche libre dans tout le catalogue NAF */}
            {isFrance && (
              <Popover open={otherPickerOpen} onOpenChange={setOtherPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-testid="btn-preset-naf-autre"
                    className={`inline-flex items-center gap-2 rounded-md px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs font-medium border transition-all ${
                      customNafSelected.length > 0
                        ? "bg-brand-moss text-white border-brand-moss shadow-sm"
                        : "bg-white text-brand-ink/85 border-brand-line border-dashed hover:border-brand-ink/25"
                    }`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Autre métier</span>
                    {customNafSelected.length > 0 && (
                      <Badge className="bg-white text-brand-ink text-[10px] px-1.5 py-0 ml-1">
                        {customNafSelected.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-[340px] sm:w-[420px]" data-testid="preset-other-popover">
                  <Command>
                    <CommandInput placeholder="Rechercher un métier (ex: plombier, coiffure, boulanger...)" data-testid="preset-other-search" />
                    <CommandList className="max-h-[340px]">
                      <CommandEmpty>Aucun métier trouvé.</CommandEmpty>
                      {NAF_CATEGORIES.map((cat) => {
                        const items = NAF_CATALOG.filter((n) => n.cat === cat);
                        return (
                          <CommandGroup key={cat} heading={cat}>
                            {items.map((n) => {
                              const selected = customNafSelected.includes(n.code);
                              return (
                                <CommandItem
                                  key={n.code}
                                  value={`${n.label} ${n.code} ${n.cat}`}
                                  onSelect={() => toggleCustomNaf(n.code)}
                                  data-testid={`preset-other-item-${n.code}`}
                                  className="cursor-pointer"
                                >
                                  <div className={`h-4 w-4 rounded border flex items-center justify-center ${selected ? "bg-brand-moss border-brand-moss" : "border-brand-line"}`}>
                                    {selected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <span className="flex-1 text-sm">{n.label}</span>
                                  <span className="font-mono text-[10px] text-brand-subtle">{n.code}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        );
                      })}
                    </CommandList>
                    {customNafSelected.length > 0 && (
                      <div className="flex items-center justify-between border-t border-brand-line px-3 py-2">
                        <span className="text-[11px] text-brand-subtle font-mono">
                          {customNafSelected.length} métier{customNafSelected.length > 1 ? "s" : ""} sélectionné{customNafSelected.length > 1 ? "s" : ""}
                        </span>
                        <button
                          onClick={() => setCustomNafSelected([])}
                          data-testid="preset-other-clear"
                          className="text-[11px] font-medium text-brand-subtle hover:text-brand-ink flex items-center gap-1"
                        >
                          <X className="h-3 w-3" /> Tout effacer
                        </button>
                      </div>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Pills des métiers "Autre" sélectionnés */}
          {isFrance && customNafSelected.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {customNafSelected.map((code) => {
                const item = NAF_CATALOG.find((n) => n.code === code);
                return (
                  <Badge
                    key={code}
                    variant="secondary"
                    className="bg-brand-paper text-brand-ink/85 border border-brand-line pl-2 pr-1 py-0.5 gap-1"
                    data-testid={`preset-other-pill-${code}`}
                  >
                    <span className="text-[11px]">{item?.label || code}</span>
                    <span className="font-mono text-[9px] text-brand-subtle">{code}</span>
                    <button onClick={() => toggleCustomNaf(code)} className="hover:bg-brand-line rounded-full p-0.5">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}

          {isFrance ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-2">
              <div className="sm:col-span-2">
                <GeoPicker value={geoSelection} onChange={setGeoSelection} />
              </div>
              <div>
                <Label className="eyebrow">Codes NAF (libre)</Label>
                <Input placeholder="Ex: 43.32A, 81.30Z" value={nafInput} onChange={(e) => setNafInput(e.target.value)} data-testid="search-input-naf" className="mt-1 h-10 font-mono text-sm border-brand-line rounded-md focus-visible:ring-brand-moss/25" />
              </div>
              <div>
                <Label className="eyebrow">Ancienneté min. (années)</Label>
                <Input type="number" min="0" placeholder="Ex: 3" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} data-testid="search-input-age" className="mt-1 h-10 font-mono text-sm border-brand-line rounded-md focus-visible:ring-brand-moss/25" />
              </div>
              <div>
                <Label className="eyebrow">Effectif minimum</Label>
                <Select value={workforceMin || "any"} onValueChange={(v) => setWorkforceMin(v === "any" ? "" : v)}>
                  <SelectTrigger data-testid="search-select-workforce-min" className="mt-1 h-10 border-brand-line rounded-md"><SelectValue placeholder="Toutes tranches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Toutes tranches</SelectItem>
                    {WORKFORCE_OPTIONS.map((o) => (<SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Effectif maximum</Label>
                <Select value={workforceMax || "any"} onValueChange={(v) => setWorkforceMax(v === "any" ? "" : v)}>
                  <SelectTrigger data-testid="search-select-workforce-max" className="mt-1 h-10 border-brand-line rounded-md"><SelectValue placeholder="Toutes tranches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Toutes tranches</SelectItem>
                    {WORKFORCE_OPTIONS.map((o) => (<SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-2">
              <div>
                <Label className="eyebrow">Mots-clés métier</Label>
                <Input placeholder="Ex: menuisier, cuisiniste, garage" value={keywords} onChange={(e) => setKeywords(e.target.value)} data-testid="search-input-keywords" className="mt-1 h-10 text-sm border-brand-line rounded-md focus-visible:ring-brand-moss/25" />
              </div>
              <div>
                <Label className="eyebrow">Villes</Label>
                <Input placeholder="Ex: Bruxelles, Liège, Genève" value={cities} onChange={(e) => setCities(e.target.value)} data-testid="search-input-cities" className="mt-1 h-10 text-sm border-brand-line rounded-md focus-visible:ring-brand-moss/25" />
              </div>
              <div className="sm:col-span-2 text-xs text-brand-subtle bg-brand-paper border border-brand-line rounded-md p-3 leading-relaxed">
                <strong>Note :</strong> hors France, la source SIRENE n'existe pas. La recherche se fait directement via Google Places (sans code NAF, sans tranche d'effectif).
              </div>
            </div>
          )}

          {/* Filtres communs : téléphone recherché + objectif de prospects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-2 pt-6 border-t border-brand-line">
            <div>
              <Label className="eyebrow">Type de téléphone</Label>
              <Select value={phoneFilter} onValueChange={setPhoneFilter}>
                <SelectTrigger data-testid="search-select-phone-filter" className="mt-1 h-10 border-brand-line rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous (sans filtre)</SelectItem>
                  <SelectItem value="any">Avec téléphone uniquement</SelectItem>
                  <SelectItem value="mobile">Mobile 06 / 07 uniquement</SelectItem>
                  <SelectItem value="fixe">Fixe uniquement (01-05, 09)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Nombre de prospects visé</Label>
              <Input
                type="number" min="1" max="100" placeholder="Ex: 100"
                value={targetCount}
                onChange={(e) => setTargetCount(e.target.value)}
                data-testid="search-input-target-count"
                className="mt-1 h-10 font-mono text-sm border-brand-line rounded-md focus-visible:ring-brand-moss/25"
              />
            </div>
            <div>
              <Label className="eyebrow">CA minimum (€)</Label>
              <Input
                type="number" min="0" step="10000" placeholder="Ex: 500000"
                value={caMin} onChange={(e) => setCaMin(e.target.value)}
                data-testid="search-input-ca-min"
                className="mt-1 h-10 font-mono text-sm border-brand-line rounded-md"
              />
            </div>
            <div>
              <Label className="eyebrow">CA maximum (€)</Label>
              <Input
                type="number" min="0" step="10000" placeholder="Ex: 5000000"
                value={caMax} onChange={(e) => setCaMax(e.target.value)}
                data-testid="search-input-ca-max"
                className="mt-1 h-10 font-mono text-sm border-brand-line rounded-md"
              />
            </div>
            <div className="sm:col-span-2 flex items-end">
              <p className="text-[11px] text-brand-subtle leading-snug">
                Si la zone saisie ne contient pas assez d'entreprises correspondantes, la recherche
                s'élargit automatiquement : codes postaux → département → départements voisins → région → France entière.
                Le chiffre d'affaires provient des comptes publiés (RNE) : toutes les sociétés n'en déposent pas.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-6 mt-2 border-t border-brand-line">
            <Button onClick={runSearch} disabled={loading} data-testid="btn-search-sirene" className="h-11 px-6 bg-brand-moss hover:bg-brand-moss2 text-white rounded-md flex-1 sm:flex-none transition-colors">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" strokeWidth={1.75} />}
              Lancer la recherche
            </Button>
            <Button variant="outline" onClick={resetFilters} data-testid="btn-reset-filters" className="h-11 px-4 border-brand-line text-brand-ink hover:bg-brand-paper hover:border-brand-ink/20 rounded-md">
              <RotateCcw className="h-4 w-4 sm:mr-2" strokeWidth={1.75} /> <span className="hidden sm:inline">Réinitialiser</span>
            </Button>
            <div className="ml-auto text-[11px] text-brand-subtle font-mono hidden md:block">
              {isFrance && activeNafCodes.length > 0 && <span>NAF : {activeNafCodes.join(", ")}</span>}
              {!isFrance && activeKeywords.length > 0 && <span>Mots-clés : {activeKeywords.join(", ")}</span>}
            </div>
          </div>
        </Card>

        {/* Bandeau d'élargissement */}
        {expansion && (
          <div
            data-testid="expansion-banner"
            className={`mb-6 rounded-md border px-4 py-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1.5 rise ${
              expansion.reached
                ? "bg-brand-mossSoft border-brand-moss/20 text-brand-moss"
                : "bg-[#FAF8F1] border-[#E9E2CF] text-[#6B5B33]"
            }`}
          >
            <span className="font-semibold">
              {expansion.reached
                ? `Objectif ${expansion.target} atteint`
                : expansion.budget_exhausted
                  ? `${expansion.obtained}/${expansion.target} prospects trouvés (quota Google Places atteint pour cette recherche)`
                  : `${expansion.obtained}/${expansion.target} prospects trouvés (France entière balayée)`}
            </span>
            {expansion.phone_filter !== "all" && (
              <Badge variant="secondary" className="bg-white/70 border text-[10px] font-mono">
                Tél : {expansion.phone_filter === "mobile" ? "mobile 06/07" : expansion.phone_filter === "fixe" ? "fixe" : "avec téléphone"}
              </Badge>
            )}
            <span className="font-mono text-[11px] opacity-80">
              {expansion.stages
                .filter((s) => s.found > 0)
                .map((s) => `${s.label}${s.areas?.length ? ` (${s.areas.slice(0, 8).join(", ")})` : ""} : ${s.found}`)
                .join(" · ") || "Aucun résultat"}
            </span>
          </div>
        )}

        {/* Table filters bar */}
        <Card className="p-4 sm:p-5 mb-6 bg-white border-brand-line rounded-md shadow-card">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-2 eyebrow hover:text-brand-ink transition-colors"
              data-testid="btn-toggle-filters"
            >
              <Filter className="h-3.5 w-3.5" strokeWidth={1.75} /> Filtres tableau
              {activeTableFilters > 0 && (
                <Badge className="bg-brand-moss text-white text-[10px] px-1.5 py-0">{activeTableFilters}</Badge>
              )}
            </button>
            {activeTableFilters > 0 && (
              <button onClick={resetTableFilters} className="text-[11px] font-medium text-brand-subtle hover:text-brand-ink flex items-center gap-1" data-testid="btn-reset-table-filters">
                <X className="h-3 w-3" /> Effacer les filtres
              </button>
            )}
          </div>

          {(filtersOpen || activeTableFilters > 0) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              <div>
                <Label className="eyebrow">Statut</Label>
                <Select value={fStatus} onValueChange={setFStatus}>
                  <SelectTrigger data-testid="filter-status" className="h-9 text-xs mt-1 border-brand-line rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {Object.entries(STATUS_META).map(([k, v]) => (<SelectItem key={k} value={k}>{v.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Téléphone</Label>
                <Select value={fPhoneKind} onValueChange={setFPhoneKind}>
                  <SelectTrigger data-testid="filter-phone" className="h-9 text-xs mt-1 border-brand-line rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="any">Avec téléphone</SelectItem>
                    <SelectItem value="mobile">Mobile (06/07)</SelectItem>
                    <SelectItem value="fixe">Fixe (01-05, 09)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Match</Label>
                <Select value={fMatch} onValueChange={setFMatch}>
                  <SelectTrigger data-testid="filter-match" className="h-9 text-xs mt-1 border-brand-line rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="haute">Haute</SelectItem>
                    <SelectItem value="moyenne">Moyenne</SelectItem>
                    <SelectItem value="faible">Faible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Score min</Label>
                <Input type="number" min="0" max="100" placeholder="Ex: 60" value={fMinScore} onChange={(e) => setFMinScore(e.target.value)} data-testid="filter-min-score" className="h-9 text-xs mt-1 font-mono border-brand-line rounded-md" />
              </div>
              <div>
                <Label className="eyebrow">Ville</Label>
                <Input placeholder="Ex: Paris" value={fCity} onChange={(e) => setFCity(e.target.value)} data-testid="filter-city" className="h-9 text-xs mt-1 border-brand-line rounded-md" />
              </div>
              <div className="flex items-end pb-0.5">
                <button
                  onClick={() => setFWithWebsite((v) => !v)}
                  data-testid="filter-website"
                  className={`w-full h-9 rounded-md border text-xs font-medium transition-colors ${fWithWebsite ? "bg-brand-moss text-white border-brand-moss" : "bg-white text-brand-ink/85 border-brand-line hover:border-brand-ink/25"}`}
                >
                  Avec site web
                </button>
              </div>
            </div>
          )}
        </Card>

        {/* Rappels en retard */}
        {overdueCount > 0 && (
          <div className="mb-5 rounded-md border border-[#EBD5CB] bg-[#FBF2EE] px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="dashboard-overdue-banner">
            <span className="flex items-center gap-2 text-xs font-semibold text-[#8A3F27]">
              <AlarmClock className="h-4 w-4" strokeWidth={1.75} />
              {overdueCount} relance{overdueCount > 1 ? "s" : ""} en retard à traiter
            </span>
            <button onClick={() => navigate("/calendrier")} data-testid="dashboard-overdue-link"
              className="ml-auto text-[10px] font-mono uppercase tracking-[0.14em] px-3 py-1.5 rounded border border-[#EBD5CB] bg-white text-[#8A3F27] hover:bg-[#F6E7E1] transition-colors">
              Voir dans le calendrier
            </button>
          </div>
        )}

        {/* Barre d'actions groupées */}
        {selectedSirets.length > 0 && (
          <div className="mb-4 rounded-md border border-brand-ink/15 bg-white shadow-card px-4 py-3 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
            <span className="text-xs font-semibold text-brand-ink" data-testid="bulk-selected-count">
              {selectedSirets.length} prospect{selectedSirets.length > 1 ? "s" : ""} sélectionné{selectedSirets.length > 1 ? "s" : ""}
            </span>
            <button onClick={() => setSelectedSirets([])} data-testid="bulk-clear"
              className="eyebrow hover:text-brand-ink transition-colors">Tout désélectionner</button>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Select value={bulkStatus} onValueChange={(v) => { setBulkStatus(v); applyBulkStatus(v); }}>
                <SelectTrigger className="h-9 w-[190px] text-xs border-brand-line rounded-md" data-testid="bulk-status-select">
                  <SelectValue placeholder="Changer le statut" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={bulkEnrichContacts} disabled={enrichingContacts} data-testid="bulk-enrich-contacts"
                className="border-brand-line rounded-md hover:bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em]">
                {enrichingContacts
                  ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  : <AtSign className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} />}
                Enrichir contacts
              </Button>
              <Button size="sm" variant="outline" onClick={() => exportCsv(selectedSirets)} data-testid="bulk-export"
                className="border-brand-line rounded-md hover:bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em]">
                <Download className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Exporter
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmDelete(true)} data-testid="bulk-delete"
                className="border-[#E9DED8] text-[#7A4B3A] rounded-md hover:bg-[#F8F3F0] text-[10px] font-mono uppercase tracking-[0.14em]">
                <Trash2 className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Supprimer
              </Button>
            </div>
          </div>
        )}

        {/* Vue : tableau ou pipeline */}
        <div className="flex items-center justify-end gap-1 mb-4 border border-brand-line rounded-md p-1 w-fit ml-auto">
          {[
            { id: "table", label: "Tableau", Icon: Table2 },
            { id: "pipeline", label: "Pipeline", Icon: LayoutGrid },
          ].map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setViewMode(id)} data-testid={`view-toggle-${id}`}
              className={`flex items-center gap-2 px-3 h-8 rounded text-[10px] font-mono uppercase tracking-[0.14em] transition-colors ${
                viewMode === id ? "bg-brand-moss text-white" : "text-brand-subtle hover:text-brand-ink"
              }`}>
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
            </button>
          ))}
        </div>

        {viewMode === "pipeline" ? (
          <PipelineBoard prospects={filteredResults} onOpen={(p) => setSelectedLead(p)} />
        ) : (
        /* Results Table */
        <Card className="bg-white border-brand-line rounded-md shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-5 sm:px-6 py-5 border-b border-brand-line">
            <div>
              <div className="font-display text-xl sm:text-2xl text-brand-ink leading-none">Prospects</div>
              <div className="eyebrow mt-2">
                {searchMode ? (
                  <>
                    {filteredResults.length}
                    {activeTableFilters > 0 && ` / ${results.length}`}
                    {" "}entreprise{filteredResults.length > 1 ? "s" : ""} · résultats de recherche
                  </>
                ) : (
                  <>
                    {filteredResults.length}
                    {activeTableFilters > 0 && ` / ${results.length}`}
                    {" sur "}{totalProspects}{" "}entreprise{totalProspects > 1 ? "s" : ""} · page {page}/{totalPages}
                  </>
                )}
              </div>
            </div>
            <Button size="sm" variant="outline"
              onClick={() => loadProspectsPage(searchMode ? 1 : page)}
              disabled={listLoading}
              data-testid="btn-refresh-prospects"
              className="border-brand-line rounded-md hover:bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em]">
              {listLoading ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} />}
              {searchMode ? "Retour à la liste" : "Actualiser"}
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px]">
              <thead>
                <tr className="bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em] text-brand-subtle border-b border-brand-line">
                  <th className="px-3 py-3 w-10">
                    <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll}
                      data-testid="select-all-prospects" aria-label="Tout sélectionner"
                      className="border-brand-line data-[state=checked]:bg-brand-moss data-[state=checked]:border-brand-moss" />
                  </th>
                  <th className="text-center px-3 sm:px-4 py-3 w-16">Score</th>
                  <th className="text-left px-3 sm:px-4 py-3">Entreprise</th>
                  <th className="text-left px-3 sm:px-4 py-3 hidden md:table-cell">NAF</th>
                  <th className="text-left px-3 sm:px-4 py-3">Ville</th>
                  <th className="text-left px-3 sm:px-4 py-3 hidden lg:table-cell">Effectif / Âge</th>
                  <th className="text-right px-3 sm:px-4 py-3 hidden xl:table-cell">CA</th>
                  <th className="text-left px-3 sm:px-4 py-3 hidden md:table-cell">Google</th>
                  <th className="text-left px-3 sm:px-4 py-3">Contact</th>
                  <th className="text-center px-3 sm:px-4 py-3 hidden lg:table-cell">Match</th>
                  <th className="text-left px-3 sm:px-4 py-3">Statut</th>
                  <th className="text-right px-3 sm:px-4 py-3">Appel</th>
                </tr>
              </thead>
              <tbody data-testid="prospects-table-body">
                {filteredResults.length === 0 && (
                  <tr><td colSpan={12} className="text-center py-12 text-brand-subtle/60 text-sm">
                    {results.length === 0 ? "Aucun prospect. Lancez une recherche pour commencer." : "Aucun résultat avec ces filtres."}
                  </td></tr>
                )}
                {filteredResults.map((p) => {
                  const pk = phoneKind(p.google_phone || "");
                  return (
                    <tr
                      key={p.siret || p.id}
                      data-testid={`prospect-row-${p.siret}`}
                      className={`data-table-row border-b border-brand-line hover:bg-brand-paper cursor-pointer ${
                        selectedSirets.includes(p.siret) ? "bg-brand-mossSoft/60" : ""
                      }`}
                      onClick={() => setSelectedLead(p)}
                    >
                      <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedSirets.includes(p.siret)} onCheckedChange={() => toggleSelect(p.siret)}
                          data-testid={`select-prospect-${p.siret}`} aria-label={`Sélectionner ${p.nom}`}
                          className="border-brand-line data-[state=checked]:bg-brand-moss data-[state=checked]:border-brand-moss" />
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 text-center">
                        <div className={`inline-flex items-center justify-center rounded-md border font-mono font-bold text-sm px-2 py-0.5 ${scoreBadge(p.priority_score)}`}>
                          {p.priority_score}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 max-w-[14rem]">
                        <div className="font-semibold text-brand-ink text-xs sm:text-sm truncate">{p.nom}</div>
                        <div className="text-[10px] font-mono text-brand-subtle truncate">{p.siret}</div>
                        {p.dirigeant_principal && (
                          <div className="text-[10px] font-mono text-brand-moss truncate mt-0.5" data-testid={`dirigeant-${p.siret}`}>
                            {p.dirigeant_principal}
                          </div>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 hidden md:table-cell">
                        <div className="font-mono text-xs font-medium text-brand-ink">{p.code_naf || "—"}</div>
                        <div className="text-[10px] text-brand-subtle truncate max-w-[10rem]">{p.libelle_naf}</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3.5">
                        <div className="text-xs sm:text-sm text-brand-ink truncate max-w-[8rem]">{p.ville || "—"}</div>
                        <div className="text-[10px] font-mono text-brand-subtle">{p.departement} {p.code_postal && `· ${p.code_postal}`}</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 hidden lg:table-cell">
                        <div className="text-xs text-brand-ink">{p.effectif_label || "—"}</div>
                        <div className="text-[10px] font-mono text-brand-subtle">{p.age_years ? `${p.age_years} ans` : "—"}</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 hidden xl:table-cell text-right" data-testid={`ca-${p.siret}`}>
                        <div className={`font-mono text-xs ${p.ca ? "text-brand-ink" : "text-brand-subtle/50"}`}>{formatCa(p.ca)}</div>
                        {p.finances_year && <div className="text-[10px] font-mono text-brand-subtle/70">{p.finances_year}</div>}
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 hidden md:table-cell">
                        {p.google_rating ? (
                          <div className="flex items-center gap-1 text-xs">
                            <Star className="h-3 w-3 fill-brand-moss text-brand-moss" />
                            <span className="font-mono font-semibold text-brand-ink">{p.google_rating}</span>
                            <span className="text-brand-subtle">({p.google_reviews_count})</span>
                          </div>
                        ) : (<span className="text-[11px] text-brand-subtle/60">—</span>)}
                      </td>
                      <td className="px-3 sm:px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          {p.google_phone && (pk === "mobile" ? <Smartphone className="h-3.5 w-3.5 text-brand-moss" /> : <Phone className="h-3.5 w-3.5 text-brand-moss" />)}
                          {p.google_website && <Globe className="h-3.5 w-3.5 text-brand-subtle" />}
                          {!p.google_phone && !p.google_website && <span className="text-[10px] text-brand-subtle/60">—</span>}
                        </div>
                        {p.google_phone && <div className="font-mono text-[10px] sm:text-[11px] text-brand-ink/85 mt-0.5 truncate max-w-[7rem]">{p.google_phone}</div>}
                        {(() => {
                          const direct = p.direct_contact;
                          if (!direct || (!direct.phone && !direct.email)) return null;
                          return (
                            <div className="mt-1" data-testid={`direct-contact-${p.siret}`}>
                              <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase tracking-[0.1em] ${
                                p.has_direct_mobile
                                  ? "bg-brand-mossSoft text-brand-moss border-brand-moss/30"
                                  : "bg-brand-paper text-brand-subtle border-brand-line"}`}>
                                {p.has_direct_mobile ? <Smartphone className="h-3 w-3" strokeWidth={1.75} /> : <AtSign className="h-3 w-3" strokeWidth={1.75} />}
                                {p.has_direct_mobile ? "Mobile direct" : "Contact direct"}
                              </div>
                              <div className="font-mono text-[10px] text-brand-ink/80 truncate max-w-[8rem] mt-0.5">
                                {direct.phone || direct.email}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 text-center hidden lg:table-cell">
                        <Badge className={`${(MATCH_META[p.match_level] || MATCH_UNKNOWN).cls} border text-[10px] font-mono uppercase`}>
                          {(MATCH_META[p.match_level] || MATCH_UNKNOWN).label}
                        </Badge>
                        <div className="text-[10px] font-mono text-brand-subtle/60 mt-0.5">{p.match_score ?? "—"}%</div>
                      </td>
                      <td className="px-3 sm:px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <Select value={p.status} onValueChange={(v) => updateStatus(p.siret, v)}>
                          <SelectTrigger data-testid={`status-select-${p.siret}`} className={`h-7 text-[10px] sm:text-xs border ${STATUS_META[p.status]?.cls || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_META).map(([k, v]) => (
                              <SelectItem key={k} value={k}>
                                <span className="inline-flex items-center gap-2">
                                  <span className={`h-2 w-2 rounded-full ${v.dot}`} />
                                  {v.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isOverdue(p.next_action_at) && (
                          <div className={`mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase tracking-[0.12em] ${OVERDUE_CLS}`}
                            data-testid={`row-overdue-${p.siret}`}>
                            <AlarmClock className="h-3 w-3" strokeWidth={1.75} />
                            Retard · {new Date(p.next_action_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                          </div>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setCallTarget(p)}
                          data-testid={`btn-call-report-${p.siret}`}
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-brand-ink text-white text-[10px] font-mono uppercase tracking-[0.14em] hover:bg-brand-moss transition-colors"
                        >
                          <Headphones className="h-3.5 w-3.5" strokeWidth={1.75} /> Fiche appel
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!searchMode && totalPages > 1 && (
            <PaginationBar
              page={page}
              totalPages={totalPages}
              onChange={(p) => loadProspectsPage(p)}
              disabled={listLoading}
            />
          )}
        </Card>
        )}
      </main>

      <CallReportModal
        prospect={callTarget}
        open={!!callTarget}
        onOpenChange={(o) => !o && setCallTarget(null)}
        onSaved={onCallSaved}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-white border-brand-line rounded-md" data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-brand-ink">
              Supprimer {selectedSirets.length} prospect{selectedSirets.length > 1 ? "s" : ""} ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-brand-subtle text-sm">
              Les fiches, notes, RDV et rappels associés seront définitivement supprimés
              (les RDV déjà envoyés dans Google Calendar seront également retirés de votre agenda).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-brand-line rounded-md" data-testid="bulk-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSelected} data-testid="bulk-delete-confirm"
              className="bg-[#7A4B3A] hover:bg-[#663D2E] text-white rounded-md">
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lead Detail Drawer */}
      <Sheet open={!!selectedLead} onOpenChange={(o) => !o && setSelectedLead(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto w-full" data-testid="lead-drawer">
          {selectedLead && (
            <LeadDetail lead={selectedLead}
              onStatusChange={(s) => updateStatus(selectedLead.siret, s)}
              onNotesSave={(n) => saveNotes(selectedLead.siret, n)}
              onCall={() => setCallTarget(selectedLead)}
              onRefresh={() => refreshEnrichment(selectedLead.siret)}
              onEnrichContacts={() => enrichContacts(selectedLead.siret)}
              enrichingContacts={enrichingContacts}
              refreshing={refreshing} />
          )}
        </SheetContent>
      </Sheet>

      {/* History Drawer */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent className="sm:max-w-md overflow-y-auto w-full">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">Historique des recherches</SheetTitle>
            <SheetDescription>Rechargez une recherche précédente en un clic.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {history.length === 0 && <div className="text-sm text-brand-subtle/60 text-center py-8">Aucune recherche enregistrée</div>}
            {history.map((h) => (
              <button key={h.id} onClick={() => loadHistoryItem(h)} data-testid={`history-item-${h.id}`}
                className="block w-full text-left p-4 rounded-md border border-brand-line hover:border-brand-ink/25 hover:bg-brand-paper transition-colors">
                <div className="font-medium text-sm text-brand-ink truncate">{h.label}</div>
                <div className="text-[11px] font-mono text-brand-subtle mt-1">
                  {h.results_count} résultats · {new Date(h.created_at).toLocaleString("fr-FR")}
                </div>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function PaginationBar({ page, totalPages, onChange, disabled }) {
  const pages = useMemo(() => {
    const span = 1;
    const items = new Set([1, totalPages, page - span, page, page + span]);
    const arr = [...items].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const withGaps = [];
    arr.forEach((n, i) => {
      if (i > 0 && n - arr[i - 1] > 1) withGaps.push("…");
      withGaps.push(n);
    });
    return withGaps;
  }, [page, totalPages]);

  return (
    <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-t border-brand-line" data-testid="prospects-pagination">
      <div className="eyebrow text-brand-subtle">Page {page} / {totalPages}</div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={disabled || page <= 1}
          data-testid="pagination-prev"
          className="h-8 px-3 rounded border border-brand-line text-[10px] font-mono uppercase tracking-[0.14em] text-brand-ink hover:bg-brand-paper disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Précédent
        </button>
        {pages.map((p, i) => (
          p === "…" ? (
            <span key={`gap-${i}`} className="px-2 text-brand-subtle text-xs">…</span>
          ) : (
            <button
              type="button"
              key={p}
              onClick={() => onChange(p)}
              disabled={disabled || p === page}
              data-testid={`pagination-page-${p}`}
              className={`h-8 min-w-[2rem] px-2 rounded border text-xs font-mono ${
                p === page
                  ? "bg-brand-moss text-white border-brand-moss"
                  : "border-brand-line text-brand-ink hover:bg-brand-paper"
              }`}
            >
              {p}
            </button>
          )
        ))}
        <button
          type="button"
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={disabled || page >= totalPages}
          data-testid="pagination-next"
          className="h-8 px-3 rounded border border-brand-line text-[10px] font-mono uppercase tracking-[0.14em] text-brand-ink hover:bg-brand-paper disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Suivant
        </button>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card className="p-4 sm:p-5 bg-white border-brand-line rounded-md shadow-card transition-colors hover:border-brand-ink/15" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow truncate">{label}</div>
          <div className={`mt-2 font-display text-2xl sm:text-3xl leading-none ${accent ? "text-brand-moss" : "text-brand-ink"}`}>
            {value}
          </div>
        </div>
        <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${accent ? "text-brand-moss" : "text-brand-subtle"}`} strokeWidth={1.5} />
      </div>
    </Card>
  );
}

function LeadDetail({ lead, onStatusChange, onNotesSave, onCall, onRefresh, refreshing, onEnrichContacts, enrichingContacts }) {
  const [converting, setConverting] = useState(false);
  const navigate = useNavigate();
  async function convertToOpportunity() {
    setConverting(true);
    try {
      const r = await axios.post(`${API}/commercial/opportunities/from-prospect`,
        { siret: lead.siret });
      toast.success(r.data.created ? "Opportunité créée" : "Opportunité existante");
      navigate(`/commercial/opportunites/${r.data.opportunity_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion impossible");
    } finally { setConverting(false); }
  }
  const [notes, setNotes] = useState(lead.notes || "");
  useEffect(() => setNotes(lead.notes || ""), [lead.siret, lead.notes]);
  const bd = lead.score_breakdown || {};
  return (
    <div>
      <SheetHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 pr-6 min-w-0">
            <SheetTitle className="font-display text-xl sm:text-2xl truncate">{lead.nom}</SheetTitle>
            <SheetDescription className="font-mono text-[11px] truncate">SIRET {lead.siret}</SheetDescription>
          </div>
          <div className={`inline-flex items-center justify-center rounded-md border font-mono font-bold text-base sm:text-lg px-3 py-1.5 flex-shrink-0 ${scoreBadge(lead.priority_score)}`}>
            {lead.priority_score}
          </div>
        </div>
      </SheetHeader>

      <div className="mt-8 space-y-7">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={onCall} data-testid="drawer-call-report"
            className="bg-brand-ink hover:bg-brand-moss text-white rounded-md text-[10px] font-mono uppercase tracking-[0.14em]">
            <Headphones className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Fiche appel
          </Button>
          <Button size="sm" variant="outline" onClick={() => printProspect(lead)} data-testid="drawer-print-prospect"
            className="border-brand-line rounded-md hover:bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em]">
            <Printer className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Imprimer la fiche
          </Button>
          <Button size="sm" variant="outline" onClick={() => printCallSheet(lead)} data-testid="drawer-print-callsheet"
            className="border-brand-line rounded-md hover:bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em]">
            <Printer className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Fiche d'appel
          </Button>
          <Button size="sm" variant="outline" onClick={onRefresh} disabled={refreshing} data-testid="drawer-refresh-enrichment"
            className="border-brand-line rounded-md hover:bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em]">
            {refreshing
              ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              : <RefreshCw className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} />}
            Rafraîchir l'enrichissement
          </Button>
          <Button size="sm" onClick={convertToOpportunity} disabled={converting}
            data-testid="drawer-convert-opportunity"
            className="bg-brand-moss hover:bg-brand-ink text-white rounded-md text-[10px] font-mono uppercase tracking-[0.14em]">
            {converting
              ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              : <TrendingUp className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} />}
            Convertir en opportunité
          </Button>
        </div>

        <div>
          <div className="eyebrow mb-2">Décomposition du score</div>
          <div className="space-y-2.5">
            <ScoreBar label="Effectif" value={bd.workforce || 0} max={25} />
            <ScoreBar label="Ancienneté" value={bd.age || 0} max={20} />
            <ScoreBar label="Maturité digitale" value={bd.digital || 0} max={30} />
            <ScoreBar label="Contactabilité" value={bd.contact || 0} max={25} />
            <ScoreBar label="Contact direct dirigeant" value={bd.contact_direct || 0} max={15} />
          </div>
        </div>

        <Separator />
        <div>
          <div className="eyebrow mb-2">Fiche source</div>
          <div className="text-sm space-y-1">
            {lead.code_naf && <div><span className="text-brand-subtle">NAF :</span> <span className="font-mono">{lead.code_naf}</span> — {lead.libelle_naf}</div>}
            <div><span className="text-brand-subtle">Adresse :</span> {lead.adresse}{lead.code_postal && `, ${lead.code_postal}`} {lead.ville}</div>
            {lead.effectif_label && <div><span className="text-brand-subtle">Effectif :</span> {lead.effectif_label}</div>}
            {lead.date_creation && <div><span className="text-brand-subtle">Créée :</span> {lead.date_creation.slice(0, 10)} ({lead.age_years} ans)</div>}
            {lead.ca !== null && lead.ca !== undefined && (
              <div data-testid="drawer-ca">
                <span className="text-brand-subtle">Chiffre d'affaires :</span> {formatCa(lead.ca)}
                {lead.finances_year ? ` (${lead.finances_year})` : ""}
                {lead.resultat_net !== null && lead.resultat_net !== undefined && (
                  <span className="text-brand-subtle"> · Résultat net {formatCa(lead.resultat_net)}</span>
                )}
              </div>
            )}
            {(lead.dirigeants || []).length > 0 && (
              <div data-testid="drawer-dirigeants" className="pt-1">
                <div className="eyebrow mb-2">Dirigeants (RNE)</div>
                <div className="space-y-1.5">
                  {lead.dirigeants.map((d, i) => (
                    <div key={i} className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-brand-ink">
                        {`${d.prenoms || ""} ${d.nom || ""}`.trim()}
                      </span>
                      {d.qualite && <span className="text-[11px] font-mono text-brand-subtle">{d.qualite}</span>}
                      {d.annee_naissance && <span className="text-[10px] font-mono text-brand-subtle/70">{d.annee_naissance}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <Separator />
        <ContactsSection lead={lead} onEnrichContacts={onEnrichContacts} enriching={enrichingContacts} />

        <Separator />
        <div>
          <div className="eyebrow mb-2">Enrichissement Google Places</div>
          {lead.google_name ? (
            <div className="text-sm space-y-1">
              <div className="font-semibold">{lead.google_name}</div>
              <div className="text-brand-subtle flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span className="break-words">{lead.google_address}</span></div>
              {lead.google_phone && <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-brand-moss" /><a href={`tel:${lead.google_phone.replace(/\s/g, '')}`} className="font-mono hover:underline">{lead.google_phone}</a></div>}
              {lead.google_website && (
                <a href={lead.google_website} target="_blank" rel="noreferrer" className="text-brand-moss hover:underline text-xs flex items-center gap-1 break-all">
                  <Globe className="h-3.5 w-3.5 flex-shrink-0" /> {lead.google_website} <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </a>
              )}
              {lead.google_rating && (
                <div className="flex items-center gap-1 pt-1">
                  <Star className="h-4 w-4 fill-brand-moss text-brand-moss" />
                  <span className="font-mono font-bold">{lead.google_rating}</span>
                  <span className="text-brand-subtle text-xs">· {lead.google_reviews_count} avis</span>
                </div>
              )}
              {lead.google_maps_url && (
                <a href={lead.google_maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-subtle hover:text-brand-ink mt-1">
                  Ouvrir dans Google Maps <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ) : (<div className="text-sm text-brand-subtle/60">Aucune correspondance trouvée sur Google Places.</div>)}
        </div>

        <Separator />
        <div>
          <div className="eyebrow mb-2">Audit du matching</div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className={`${(MATCH_META[lead.match_level] || MATCH_UNKNOWN).cls} border`}>{(MATCH_META[lead.match_level] || MATCH_UNKNOWN).label}</Badge>
            <span className="font-mono text-sm font-bold">{lead.match_score}%</span>
          </div>
          <div className="text-xs text-brand-subtle font-mono">
            Nom : {lead.match_name_score}% · Adresse : {lead.match_address_score}%
            {lead.match_postal && " · Code postal identique"}
          </div>
        </div>

        <Separator />
        <div>
          <Label className="eyebrow">Statut de prospection</Label>
          <Select value={lead.status} onValueChange={onStatusChange}>
            <SelectTrigger className="mt-1 h-10 border-brand-line rounded-md" data-testid="drawer-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  <span className="inline-flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${v.dot}`} />
                    {v.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="eyebrow">Notes commerciales</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="drawer-notes-textarea" className="mt-1 min-h-[90px] border-brand-line rounded-md" placeholder="Ex: RDV le 12/03 avec le dirigeant..." />
          <Button size="sm" className="mt-3 h-10 px-5 bg-brand-moss hover:bg-brand-moss2 text-white rounded-md w-full sm:w-auto" onClick={() => onNotesSave(notes)} data-testid="drawer-notes-save">
            Sauvegarder
          </Button>
        </div>
      </div>
    </div>
  );
}

const CONF_META = {
  verifie: { label: "Vérifié · site", cls: "bg-brand-mossSoft text-brand-moss border-brand-moss/30" },
  probable: { label: "Probable", cls: "bg-[#FBF6EE] text-[#8A6A2F] border-[#E8DCC4]" },
  "": { label: "À vérifier", cls: "bg-brand-paper text-brand-subtle border-brand-line" },
};

function ContactsSection({ lead, onEnrichContacts, enriching }) {
  const contacts = lead.dirigeants_contacts || [];
  const emails = lead.site_emails || [];
  const phones = lead.site_phones || [];
  const searchLinks = (name) => {
    const q = encodeURIComponent(`${name} ${lead.nom} ${lead.ville || ""}`.trim());
    return [
      { label: "Google", href: `https://www.google.com/search?q=${q}`, Icon: Search },
      { label: "LinkedIn", href: `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(`${name} ${lead.nom}`)}`, Icon: Linkedin },
      { label: "Pages Jaunes", href: `https://www.pagesjaunes.fr/recherche/${encodeURIComponent(lead.ville || "france")}/${encodeURIComponent(lead.nom)}`, Icon: Phone },
    ];
  };

  return (
    <div data-testid="drawer-contacts">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="eyebrow">Contacts & lignes directes</div>
          <div className="text-[11px] text-brand-subtle/80 mt-1">
            Sources publiques uniquement (site de l'entreprise) — gratuit, aucun crédit
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={onEnrichContacts} disabled={enriching || !lead.google_website}
          data-testid="drawer-enrich-contacts"
          className="border-brand-line rounded-md hover:bg-brand-paper text-[10px] font-mono uppercase tracking-[0.14em] flex-shrink-0">
          {enriching
            ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
            : <AtSign className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} />}
          Enrichir
        </Button>
      </div>

      {(lead.dirigeants || []).length === 0 && (
        <div className="text-sm text-brand-subtle/60">Aucun dirigeant connu pour cette société.</div>
      )}

      <div className="space-y-3">
        {(contacts.length ? contacts : (lead.dirigeants || [])).map((d, i) => {
          const name = `${d.prenoms || ""} ${d.nom || ""}`.trim();
          return (
            <div key={i} className="rounded-md border border-brand-line p-3" data-testid={`contact-card-${i}`}>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm text-brand-ink font-semibold">{name}</span>
                {d.qualite && <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-brand-subtle">{d.qualite}</span>}
                {d.poste_site && (
                  <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-brand-moss" data-testid={`contact-poste-${i}`}>
                    · {d.poste_site} (site)
                  </span>
                )}
              </div>

              {d.email && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <a href={`mailto:${d.email}`} className="text-sm font-mono text-brand-moss hover:underline break-all">{d.email}</a>
                  <Badge className={`${CONF_META[d.email_confidence || ""].cls} border text-[9px] font-mono uppercase tracking-[0.1em]`}>
                    <ShieldCheck className="h-3 w-3 mr-1" strokeWidth={1.75} />
                    {CONF_META[d.email_confidence || ""].label}
                  </Badge>
                </div>
              )}
              {d.phone && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <a href={`tel:${d.phone.replace(/\s/g, "")}`} className="text-sm font-mono text-brand-ink hover:underline">{d.phone}</a>
                  <Badge className={`${CONF_META[d.phone_confidence || ""].cls} border text-[9px] font-mono uppercase tracking-[0.1em]`}>
                    {CONF_META[d.phone_confidence || ""].label}
                  </Badge>
                </div>
              )}
              {d.linkedin && (
                <a href={d.linkedin} target="_blank" rel="noreferrer" data-testid={`contact-linkedin-${i}`}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs text-brand-moss hover:underline break-all">
                  <Linkedin className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} /> Profil LinkedIn <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {!d.email && !d.phone && (
                <div className="mt-1 text-[11px] text-brand-subtle/70">Aucune coordonnée nominative publique trouvée</div>
              )}

              <div className="mt-2.5 flex flex-wrap gap-3">
                {searchLinks(name).map(({ label, href, Icon }) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer"
                    data-testid={`contact-search-${label.toLowerCase().replace(/\s/g, "-")}-${i}`}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] text-brand-subtle hover:text-brand-ink transition-colors">
                    <Icon className="h-3 w-3" strokeWidth={1.75} /> {label}
                  </a>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {(emails.length > 0 || phones.length > 0) && (
        <div className="mt-4" data-testid="drawer-site-contacts">
          <div className="eyebrow mb-2">Coordonnées publiques du site</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {emails.map((e) => (
              <a key={e} href={`mailto:${e}`} className="font-mono text-xs text-brand-moss hover:underline break-all">{e}</a>
            ))}
            {phones.map((p) => (
              <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="font-mono text-xs text-brand-ink hover:underline">{p}</a>
            ))}
          </div>
        </div>
      )}

      {lead.contacts_enriched_at && emails.length === 0 && phones.length === 0 && (
        <div className="mt-3 text-[11px] text-brand-subtle/70" data-testid="drawer-contacts-empty">
          Aucune coordonnée publique détectée sur le site ({lead.contacts_pages_scanned || 0} page(s) analysée(s)).
        </div>
      )}
      {!lead.google_website && (
        <div className="mt-3 text-[11px] text-brand-subtle/70">Pas de site web connu : utilisez les liens de recherche ci-dessus.</div>
      )}
    </div>
  );
}

function ScoreBar({ label, value, max }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-0.5">
        <span className="text-brand-ink/85">{label}</span>
        <span className="font-mono font-bold">{value}<span className="text-brand-subtle/60">/{max}</span></span>
      </div>
      <div className="h-[3px] bg-brand-line overflow-hidden">
        <div className="h-full bg-brand-moss transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
